import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/Button';
import { cn } from 'utils/classnames';
import { DISPOSITION_COLORS } from 'utils/constants';

const COUNTDOWN_SECONDS = 5;

interface Props {
  trackingId: string;
  scanInput: ReactNode;
  onDone: () => void;
}

export const ReceiptClosedScreen = ({ trackingId, scanInput, onDone }: Props) => {
  const { t } = useTranslation();
  const [count, setCount] = useState(COUNTDOWN_SECONDS);
  const colors = DISPOSITION_COLORS.CONFIRMING;

  useEffect(() => {
    if (count <= 0) {
      onDone();
      return;
    }
    const timeout = setTimeout(() => setCount((prev) => prev - 1), 1000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  const progress = (count / COUNTDOWN_SECONDS) * 100;

  return (
    <div
      className={cn(
        '-mx-4 -mb-6 flex min-h-full flex-col items-center px-6 pb-6 pt-4',
        colors.gradient
      )}
    >
      <div className="mb-6 w-full">{scanInput}</div>

      <div className="mb-6 flex flex-col items-center gap-6 text-center">
        <div className="animate-bounce">
          <div
            className={cn(
              'flex h-28 w-28 items-center justify-center rounded-full bg-white/20 ring-4 ring-white/40 ring-offset-2',
              colors.ringOffset
            )}
          >
            <span className="text-6xl">🎉</span>
          </div>
        </div>
      </div>

      <div className="mb-4 text-center">
        <p className="text-5xl font-black uppercase tracking-widest text-white leading-none">
          {t('receiptClosed.title')}
        </p>
        <p className="mt-3 text-base font-bold text-white/80">{t('receiptClosed.subtitle')}</p>
      </div>

      <h1 className="mb-6 text-base font-bold text-white break-all">
        {t('receiptClosed.trackingPrefix')} {trackingId}
      </h1>

      <div className="w-full mb-2">
        <div className="h-3 w-full rounded-full bg-white/20 overflow-hidden">
          <div
            className="h-full rounded-full bg-white transition-all duration-1000 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      <p className="mb-6 text-sm font-bold uppercase tracking-wide text-white/80">
        {t('receiptClosed.returningIn', { count })}
      </p>

      <Button
        type="button"
        className={cn(
          'h-14 w-full rounded-2xl bg-white text-lg font-bold shadow-lg hover:bg-white/90',
          colors.buttonText
        )}
        onClick={onDone}
      >
        {t('receiptClosed.continue')}
      </Button>
    </div>
  );
};
